import {MutableRefObject} from 'react';

import {CodeResponse, useGoogleLogin} from '@react-oauth/google';
import styled from 'styled-components';

import {loginWithProvider, OAuthProviders} from '~/shared/services/auth';
import {getLocalizationService} from '~/shared/services/localisationService';
import GoogleIcon from '~/shared/components/SignIn/assets/googleIcon.svg?react';
import {checkFreeEmail} from '~/shared/components/SignIn/utils/authHelpers';
import {trackEvent} from '~/shared/services/analytics';
import {LoginRegistrationProviders} from '~/shared/services/analytics/analyticsModels';
import AuthProviderButton from '~/shared/components/SignIn/AuthProviderButton';

import {LoginProvider} from './consts';

const StyledGoogleIcon = styled(GoogleIcon).attrs({
  viewBox: '0 0 25 24',
  width: 21,
  height: 20,
})``;

export interface GoogleSectionProps {
  openProviderScreen: (options: {
    providerProps: Record<string, string | number | undefined>;
    nextLoginProvider: LoginProvider;
  }) => void;
  openLoginScreen?: (options: {
    email?: string;
    getAuthCodeForProviderConnect: boolean;
    nextProvider?: LoginProvider;
    providerProps?: Record<string, string | number | undefined>;
  }) => void;
  onSubmit?: () => void;
  isRedirectedToStepRef?: MutableRefObject<boolean>;
  fromCheckout?: boolean;
  isRegistration?: boolean;
}

const GoogleSection = ({openProviderScreen, openLoginScreen, onSubmit, isRedirectedToStepRef, fromCheckout, isRegistration}: GoogleSectionProps) => {
  const {t} = getLocalizationService();

  const login = useGoogleLogin({
    flow: 'auth-code',
    redirect_uri: 'postmessage',
    onSuccess: async (authResponse: Omit<CodeResponse, 'error' | 'error_description' | 'error_uri'>) => {
      const {
        email,
        fullName,
        providerAccessToken: googleCode,
        shouldConnectAccount,
      } = (await loginWithProvider(OAuthProviders.GOOGLE, authResponse)) || {};

      const isFree = email && await checkFreeEmail(email);

      if (shouldConnectAccount) {
        if (isFree) {
          openProviderScreen({
            providerProps: {email, googleCode, fullName},
            nextLoginProvider: LoginProvider.GOOGLE,
          });
          return;
        }
        if (!isFree && openLoginScreen) {
          if (isRedirectedToStepRef) {
            isRedirectedToStepRef.current = true;
          }
          openLoginScreen({
            email,
            getAuthCodeForProviderConnect: true,
            nextProvider: LoginProvider.GOOGLE,
            providerProps: {email, googleCode, fullName},
          });
          return;
        }
      }

      if (onSubmit) {
        onSubmit();
      }
    },
    scope: 'openid email profile',
  });

  const handleLogin = () => {
    if (isRegistration) {
      trackEvent('registrationForm_click', {onCheckout: fromCheckout, authenticationProvider: LoginRegistrationProviders.GOOGLE});
    } else {
      trackEvent('loginForm_click', {onCheckout: fromCheckout, authenticationProvider: LoginRegistrationProviders.GOOGLE});
    }
    login();
  };
  
  return (
    <AuthProviderButton
      text={t('continue_with_xxx', {companyName: 'Google'})}
      icon={<StyledGoogleIcon />}
      ariaLabel={t('quick_register_with_xxx_aria_label', {companyName: 'Google'})}
      onClick={handleLogin}
    />
  );
};

export default GoogleSection;
